import { Component, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

/* ═══════════════════════════════════════════════════════════════════
   Types — mirror the original PMR_POLICIES data shape 1:1 so content
   can be copied in without restructuring.
   ═══════════════════════════════════════════════════════════════════ */
export interface PmrTable {
  head: string[];
  rows: string[][];
  hl?: number[]; // row indices to highlight
}
export interface PmrCallout {
  t: string;
  b: string[];
}
export interface PmrConsent {
  t: string;
  d: string;
}
export interface PmrBlock {
  h?: string;
  p?: string[];
  list?: string[];
  table?: PmrTable;
  callout?: PmrCallout;
  consents?: PmrConsent[];
  consentNote?: string;
  sub?: PmrBlock[];
  after?: string[];
  hl?: boolean; // top-level section highlight
}
export interface PmrDoc {
  id: string;
  title: string;
  href: string;
  updated: string;
  icon: string; // font-awesome class
  blurb: string;
  intro: string;
  body: PmrBlock[];
}

@Component({
  selector: 'app-pmr-policies',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './pmr-policies.component.html',
  styleUrls: ['./pmr-policies.component.css']
})
export class PmrPoliciesComponent implements OnInit {

  contact = {
    email: { role: 'Email', value: 'technoreachlabs2026@gmail.com',
             href: 'https://mail.google.com/mail/?view=cm&fs=1&to=technoreachlabs2026@gmail.com' 
           },
    phone: { role: 'Phone', value: '+91 80720 46800', href: 'tel:+918072046800' },
    place: { role: 'Location', value: 'Trichy, India' }
  };
  publisher = { name: 'Technoreach Labs', url: 'https://www.technoreachlabs.com/', location: 'Trichy, India' };

  order: string[] = ['privacy', 'notice', 'cookies', 'reviews', 'whatsapp', 'contact'];
  activeId = 'privacy';
  mobileNavOpen = false;

  docs: Record<string, PmrDoc> = {

    /* ───────── PRIVACY ───────── */
    privacy: {
      id: 'privacy', title: 'Privacy policy', href: '/policies/privacy', updated: '21 Aug 2026',
      icon: 'fa-shield-halved',
      blurb: 'What we collect, why we collect it, who else sees it, how long we keep it, and what you can ask us to do.',
      intro: 'This policy explains what we do with your personal data. It applies to customers, to vendors and their staff, and to anyone who visits PickMeRight without an account.',
      body: [
        { h: '1. Who we are', p: [
          'PickMeRight is a website published by Technoreach Labs, Trichy, India. There is no mobile app — everything runs in your browser.',
          'If you have a question about your data, or a complaint about anything on the platform, write to us at technoreachlabs2026@gmail.com or call +91 80720 46800. Our contact details are at the bottom of every policy page. If we do not resolve it, you can approach the Data Protection Board of India.'
        ]},
        { h: '2. What we collect', sub: [
          { h: 'What you give us', list: [
            'Your name, mobile number and email address when you create an account.',
            'Your profile photo and display name, if you add them.',
            'Your preferences — your city, the categories you follow (restaurants, salons, supermarkets, pharmacies, hotels, clinics, pet stores, textile) and your preferred vendors.',
            'The reviews you write, the ratings you give, and any photos or videos you upload.',
            'The broadcast requests you send to vendors.',
            'Anything you tell us when you contact support.'
          ]},
          { h: 'What we record as you use PickMeRight', list: [
            'Which vendor you scanned, when, and the category. This is what makes a review verified.',
            'The credits you earn and spend.',
            'Your browser, device type and IP address.',
            'Who invited you, and who you invited.'
          ]},
          { h: 'What we work out about you', p: [
            'Some of what we hold is calculated, not given. That includes your Purchase, Review, Network and Engagement scores and the Final Score derived from them, how recently and often you shop, which categories you favour, and whether your account has gone quiet.',
            'We use these to decide which offers to show you. You can switch this off.'
          ]}
        ]},
        { h: '3. Why we collect it',
          p: ['We process your data for these purposes and nothing else. If we want to use it for something new, we will ask you again.'],
          table: { head: ['What we use your data for', 'Is it your choice?', 'What changes if you decline'], rows: [
            ['Running your account', 'No — this is the service', 'There is no account'],
            ['Verifying purchases', 'No — this is what makes reviews trustworthy', '—'],
            ['Your credit wallet', 'No — while you hold a balance', '—'],
            ['Publishing your reviews', 'Yes', 'You can withdraw a review'],
            ['Video reviews', 'Yes — asked separately', 'Write one instead'],
            ['Scoring and segmentation', 'Yes', 'You are left out of targeted offers'],
            ['Personalised offers', 'Yes', 'You see general offers'],
            ['Broadcast requests', 'Yes', 'The feature switches off'],
            ['Referrals and leaderboard', 'Yes — asked separately', 'Your name stays private'],
            ['Messages from us', 'Yes — per channel', 'We do not message you'],
            ['Keeping the platform honest', 'No — we are required to', '—'],
            ['Improving the product', 'Yes', 'We measure less']
          ]},
          after: ['Where the table says a purpose is your choice, it sits under one of the two permissions in section 8. Personalisation and reviews share one; messages have their own.']
        },
        { h: '4. Who else sees your data',
          p: ['We do not sell your personal data.'],
          sub: [
            { h: 'Vendors',
              table: { head: ['A vendor can see', 'A vendor cannot see'], rows: [
                ['Your display name and review, on their page', 'Your mobile number or email'],
                ['That a verified purchase happened', 'What you bought, category by category'],
                ['A broadcast request you sent them', 'Your purchases at any other vendor'],
                ['Aggregate patterns across their own customers', 'Your individual scores at other vendors'],
                ['How many people an offer would reach', 'Your credit balance, or how you spend it']
              ]},
              callout: { t: 'Targeting never reveals you', b: ['When a vendor builds an offer they choose criteria and we tell them how many people match. They never receive the list.'] }
            },
            { h: 'Companies that work for us', p: [
              'Cloud hosting, messaging providers, analytics, our payment gateway and support tooling. They act on our instructions, are contractually bound, and cannot use your data for themselves.'
            ]},
            { h: 'When the law requires it', p: [
              'We disclose data where a court, regulator or law enforcement agency lawfully requires it, or to establish or defend a legal claim. Where we are allowed to tell you, we will.'
            ]}
          ]
        },
        { h: '5. Where your data is stored', p: [
          'Your data is stored on servers in India. PickMeRight operates in India only. Where a service provider processes data outside India, we use providers in countries the Indian government has not restricted and require the same standard of protection by contract.'
        ]},
        { h: '6. How long we keep it',  
          p: ['We keep data while we are using it for the purpose you agreed to, then we delete it. Some records we are required by law to keep longer.'],
          table: { head: ['What', 'How long', 'What happens then'], rows: [
            ['Your account', 'While active, and 3 years after', 'We warn you, then delete'],
            ['Verification codes', 'A few hours', 'Deleted'],
            ['Sign-in records', '1 year', 'Deleted'],
            ['Purchase and wallet records', '8 years', 'Kept — tax law'],
            ['Written reviews', 'While the vendor is listed', 'Your name removed if you leave'],
            ['Video reviews', 'While published', 'Deleted entirely on withdrawal'],
            ['Scores and segments', '24 months', 'Recalculated, not archived'],
            ['Messages we sent you', '2 years', 'Deleted'],
            ['Your permission records', '7 years', 'Kept as proof'],
            ['Backups', '35 days', 'Expire automatically']
          ]},
          after: [
            'Deleted data can sit in our backups for up to 35 days before expiring. We use backups only to recover from failure.',
            'If you have not used PickMeRight for three years we will delete your data. At least 48 hours before, we send a notice with the date and one link to keep your account. Signing in resets the clock.'
          ]
        },
        { h: '7. If you close your account', 
          table: { head: ['What', 'What happens to it'], rows: [
            ['Your profile', 'Deleted after a 30 day grace period'],
            ['Your written reviews', 'Stay published, with your name and photo removed'],
            ['Your video reviews', 'Deleted completely — a face cannot be anonymised'],
            ['Your verified badges', 'The badge stays, the link to you does not'],
            ['Your credits', '[Pending business decision — we show you the balance and ask you to confirm first]'],
            ['Your referrals', 'Your name is removed; credits your friends earned are not taken back'],
            ['Leaderboard entries', 'Removed immediately'],
            ['Purchase records', 'Kept for the period tax law requires']
          ]},
          after: ['We keep reviews because other customers and vendors relied on them, and because once your name and account link are gone the review is no longer about you. If you want a specific review taken down entirely, ask us.']
        },
        { h: '8. Your choices and rights',
          p: ['Everything below is in your account, under Privacy and data.'],
          table: { head: ['You can', 'How long we take'], rows: [
            ['Delete your account', '30 days after the grace period'],
            ['Stop messages on a channel', 'Immediately'],
            ['Complain', 'Acknowledged in 24 hours, resolved in 15 days']
          ]},
          callout: { t: 'Saying no never breaks the service', b: ['Turning off an optional choice does not disable anything you need. Switch off personalised rewards and your wallet and purchase history work exactly as before.'] },
          sub: [{ h: 'The two choices you make',
            p: ['These are the permissions we ask for, in the words you see them. Neither is ticked for you, and neither is required to open an account.'],
            consents: [
              { t: 'Personalised Experience & Reviews', d: 'Allow PickMeRight to use my purchase activity, such as products, categories and purchase dates, to provide relevant rewards and personalised benefits, and to let me take part in reviews and feedback that help improve products, services and customer experience. Purchase amounts will not be recorded for this purpose.' },
              { t: 'Stay Updated with Us', d: 'I’d like to receive helpful updates from PickMeRight through WhatsApp and / or email, including rewards, benefits, offers and other customer updates.' }
            ],
            consentNote: 'You can change your preferences at any time.',
            after: [
              'The first covers two things that work together: what we learn from your purchases, and your ability to review the vendors you bought from. Both rest on the same purchase record, so they sit under one choice. Turn it off and we stop personalising, and you stop being able to write reviews — your existing reviews stay up.',
              'The second covers messages, and nothing else. Turning it off does not affect your wallet, your purchases or your reviews.'
            ]
          }]
        },
        { h: '9. Cookies', p: [
          'Necessary cookies keep you signed in and keep QR scanning secure, and they load without asking. Everything else waits until you choose. Change your choices any time using Cookie preferences in the footer. The full list is in our Cookie policy.'
        ]},
        { h: '10. Children', p: [
          'PickMeRight is for people aged 18 and over. We do not knowingly create accounts for children, and we do not profile them or show them targeted offers. If we find an account belongs to someone under 18 we close it and delete the data. If you believe a child has an account with us, write to technoreachlabs2026@gmail.com.'
        ]},
        { h: '11. How we protect your data', list: [
          'Data is encrypted when stored and when moving between systems.',
          'Mobile numbers are encrypted; only the last four digits are stored separately for display.',
          'Access is limited by role. Vendors cannot reach customer purchase data, and staff access is logged.',
          'Sessions expire after inactivity, and repeated failed sign-ins lock the account temporarily.'
        ], after: ['No system is perfectly secure and we will not claim otherwise. What we can commit to is not collecting more than we need, and telling you promptly when something goes wrong.']},
        { h: '12. If there is a breach', p: [
          'If your data is exposed we will tell you without delay — what happened, which data, what you can do, and how to reach us. We also notify the Data Protection Board of India and give full details within 72 hours.'
        ]},
        { h: '13. Changes', p: [
          'We update the date at the top whenever this changes, and keep every previous version on this page. If a change materially affects how we use your data we will not simply update the page — we will tell you and ask again.'
        ]}
      ]
    },

    /* ───────── NOTICE ───────── */
    notice: {
      id: 'notice', title: 'Privacy notice', href: '/policies/notice', updated: '21 Aug 2026',
      icon: 'fa-file-signature',
      blurb: 'The short notice shown when you sign up. Legally a separate document from the privacy policy.',
      intro: 'This is what appears on the final sign-up screen, before you create an account. It is reproduced here so you can read it without signing up.',
      body: [
        { h: 'Before you create your account',
          callout: { t: 'What we tell you at sign-up', b: [
            'We collect your name, mobile number and email so you can sign in. We record which vendors you scan and when, so your reviews count as verified. We record the credits you earn and spend.',
            'If you agree, we also work out which offers suit you, show your name on the public leaderboard, and send you offers on the channels you pick. You can change any of this later, or switch it off entirely, without losing your account or your credits.',
            'You can ask us to show you your data, correct it, or delete it. You can withdraw your permission at any time — it is as easy as giving it.'
          ]}
        },
        { h: 'The two choices you make',
          p: ['Alongside the notice you are asked two separate questions. Neither is ticked for you, and neither is required to open an account.'],
          consents: [
            { t: 'Personalised Experience & Reviews', d: 'Allow PickMeRight to use my purchase activity, such as products, categories and purchase dates, to provide relevant rewards and personalised benefits, and to let me take part in reviews and feedback that help improve products, services and customer experience. Purchase amounts will not be recorded for this purpose.' },
            { t: 'Stay Updated with Us', d: 'I’d like to receive helpful updates from PickMeRight through WhatsApp and / or email, including rewards, benefits, offers and other customer updates.' }
          ],
          consentNote: 'You can change your preferences at any time.',
          after: ['One covers what we do with your purchase activity, including your reviews. The other covers whether we message you. They are recorded separately and you can change either without touching the other.']
        },
        { h: 'Where you see it', p: ['The notice appears on the sign-up screen itself, not behind a link. You do not have to open the privacy policy to see it.'] },
        { h: 'Versions', p: ['Each version of this notice is numbered. When you agree to something, we record which version was on your screen at the time — so we can always show you exactly what you were told.'] }
      ]
    },

    /* ───────── COOKIES ───────── */
    cookies: {
      id: 'cookies', title: 'Cookie policy', href: '/policies/cookies', updated: '21 Aug 2026',
      icon: 'fa-cookie-bite',
      blurb: 'Every cookie we set, by name, category, purpose and duration.',
      intro: 'Cookies are small files a website stores on your device. We use them, and similar technologies, for the purposes below.',
      body: [
        { h: 'Your choices', p: [
          'When you first visit we ask what you will allow. Necessary cookies load either way, because without them you cannot sign in and QR scanning is not secure. Everything else waits for your answer.',
          'Change your mind at any time using Cookie preferences at the bottom of every page. We ask again after 12 months, and sooner if we add anything new to this list.'
        ], callout: { t: 'Refusing does not lock you out', b: ['You can browse vendors and read reviews having accepted nothing beyond the necessary cookies. We do not use a cookie wall.'] }},
        { h: 'The categories', table: { head: ['Category', 'Consent', 'What it does'], rows: [
          ['Necessary', 'Not required', 'Keeps you signed in, protects the QR scan, blocks automated abuse'],
          ['Preferences', 'Required', 'Remembers your city, the categories you follow, your saved filters and your date format'],
          ['Analytics', 'Required', 'Which screens people use, what is slow, what crashed'],
          ['Advertising', 'Required', 'Whether an offer led to a visit, and audiences for vendor campaigns'],
          ['Embedded media', 'Required', 'Plays video reviews and loads share buttons']
        ]}},
        { h: 'The full list', // hl: true,
          p: ['This table is generated from an automated scan of our site, so it stays accurate. Necessary cookies are set on every visit; the rest wait for your choice.'],
          table: { head: ['Name', 'Provider', 'Category', 'Expires'], rows: [
            ['[pmr_session]', 'PickMeRight', 'Necessary', 'Session'],
            ['[pmr_csrf]', 'PickMeRight', 'Necessary', 'Session'],
            ['[pmr_scan]', 'PickMeRight', 'Necessary', '30 minutes'],
            ['[pmr_prefs]', 'PickMeRight', 'Preferences', '12 months'],
            ['[_analytics]', '[Provider]', 'Analytics', '[Duration]'],
            ['[_ads]', '[Provider]', 'Advertising', '[Duration]']
          ]}
        },
        { h: 'Changing your mind later', p: ['Your choice is stored for 12 months and then we ask again. You can change it before that at any time using Cookie preferences in the footer, and the change takes effect on the next page you load.'] }
      ]
    },

    /* ───────── REVIEWS ───────── */
    reviews: {
      id: 'reviews', title: 'Review policy', href: '/policies/reviews', updated: '21 Aug 2026',
      icon: 'fa-star-half-stroke',
      blurb: 'How verification works, what credits do and do not buy, what we remove, and what vendors can do.',
      intro: 'PickMeRight exists because online reviews stopped being trustworthy. This page explains exactly how ours work. We would rather be explicit than reassuring.',
      body: [
        { h: '1. Every review comes from a real purchase', p: [
          'You cannot review a business you have not been to. To write a review you scan the vendor\'s QR code at the point of purchase, and that scan is what unlocks the review. No scan, no review.',
          'This is why our reviews carry a verified badge and most platforms\' do not. It also means the most common fake review — written by someone who was never there — is not something we moderate after the fact. It is something the platform will not let happen.'
        ]},
        { h: '2. Credits, and what they do not buy',
          p: ['You earn credits for writing a review. We want to be completely clear about how that works, because incentives are where review platforms lose their integrity.'],
          callout: { t: 'Our commitment on credits', b: [
            'You earn exactly the same credits for a one-star review as for a five-star review. The rating is not part of the calculation and never will be.',
            'We will never take credits back because you left a bad review.',
            'We will never ask you, or hint to you, that a positive review is preferred.',
            'Every review that earned credits carries a label saying so. It is permanent and cannot be dismissed.'
          ]}
        },
        { h: '3. What makes a review acceptable',
          list: [
            'Write about your own experience at that business, on that visit.',
            '“The queue was slow but staff fixed a pricing mistake without fuss” helps someone. “Bad” does not.',
            'Say if something has changed since a previous visit.'
          ],
          sub: [
            { h: 'What we remove', list: [
              'Abuse, threats, slurs or sexual content.',
              'Personal details about staff or other customers.',
              'Reviews by the business, its staff or their families without saying so.',
              'Reviews traded for anything outside the platform, or written on someone else\'s behalf.',
              'Promotion of another business, or anything unrelated to the visit.',
              'Anything unlawful, or anything an authority lawfully requires us to remove.'
            ]},
            { h: 'What we do not remove', list: [
              'A low rating.',
              'A complaint the vendor disputes, unless it breaks a rule above.',
              'Criticism that is blunt, as long as it is about the experience and not the person.'
            ]}
          ]
        },
        { h: '4. What vendors can and cannot do',
          table: { head: ['A vendor can', 'A vendor cannot'], rows: [
            ['Reply publicly to any review', 'Delete a review'],
            ['Flag a review they believe breaks the rules', 'Hide, bury or reorder reviews'],
            ['See their own rating trend', 'Pay for reviews or removals'],
            ['Ask customers to review them, generally', 'Ask only the customers they expect to be happy']
          ]},
          after: ['When a vendor flags a review a moderator checks it against the rules above. The vendor does not decide the outcome, and we tell the reviewer what happened either way.']
        },
        { h: '5. How reviews are ordered', p: ['The default order is recency and helpfulness. Rating plays no part in it. A one-star review sits exactly where a five-star review would, on the same date.'] },
        { h: '6. Moderation', p: [
          'Every review is checked automatically before it appears, and a person looks at anything flagged. If we remove your review we tell you which rule it broke, and you can ask us to look again.',
          'If someone repeatedly breaks these rules we may limit their reviewing, and in serious cases close the account. Where we do, we say why.'
        ]},
        { h: '7. Video reviews', p: [
          'A video review shows your face and voice to anyone visiting that vendor\'s page. We ask you separately before you record, and you can take it down at any time. When you do the file is deleted — unlike a written review, a video cannot be anonymised.'
        ]},
        { h: '8. Sharing your review on Google',
          p: ['After you publish a review with us, we may ask whether you would also like to post it on that vendor’s Google listing. It is a question, asked once per review, and the answer is entirely yours.'],
          list: [
            'Nothing happens unless you tap the button. We never post on your behalf, and we never pass your review to Google without you choosing to.',
            'Tapping it opens Google in a new tab, already pointed at that vendor. You write and publish there yourself, under your own Google account.',
            'What happens on Google is between you and Google, under their terms and their privacy policy. We cannot see, edit or remove anything you post there.',
            'Your review stays on PickMeRight either way. Posting on Google does not move it, copy it or replace it.',
            'Declining changes nothing at all. Your credits, your verified badge and your review are exactly the same whether you share it or not.'
          ],
          after: [
            'Vendors value visibility on Google, and some customers are happy to help. That is the whole reason the prompt exists.',
            'It has no bearing on what you earn. Credits are never increased, reduced or withheld based on whether you shared a review elsewhere — the same way they are never affected by the rating you gave.',
            'If you would rather not be asked, turn the prompt off in your account under Privacy and data.'
          ]
        }
      ]
    },

    /* ───────── GRIEVANCE ───────── */
    contact: {
      id: 'contact', title: 'Contact and complaints', href: '/policies/contact', updated: '21 Aug 2026',
      icon: 'fa-headset',
      blurb: 'How to reach us, and how long we take to answer.',
      intro: 'A review taken down, data you want removed, a vendor listing you dispute, or anything else — this page tells you how to reach us and by when we will answer.',
      body: [
        { h: 'How to reach us',
          table: { head: ['Channel', 'Detail', 'Best for'], rows: [
            ['Email', 'technoreachlabs2026@gmail.com', 'Anything, including data requests'],
            ['Phone', '+91 80720 46800', 'Urgent or account access problems'],
            ['Location', 'Trichy, India', 'Published by Technoreach Labs — technoreachlabs.com']
          ]},
          after: ['We do not run separate mailboxes for different kinds of complaint. One address reaches the person who can act on it.']
        },
        { h: 'What to include', list: [
          'Your registered mobile number or email, so we can find the account.',
          'What happened, and when.',
          'A link or screenshot, if it concerns a specific review, vendor or offer.',
          'What you would like us to do.'
        ]},
        { h: 'What happens next', list: [
          'We acknowledge within 24 hours with a reference number.',
          'We look into it and write back with a decision and the reason for it.',
          'If we need longer we tell you why and give you a date.',
          'If you are not satisfied, reply and ask us to look again. A second person reviews it.'
        ]},
        { h: 'Our published commitments', hl: true,
          table: { head: ['Request', 'Time'], rows: [
            ['Acknowledge any message', '24 hours'],
            ['Resolve a complaint', '15 days'],
            ['Show you your data', '30 days'],
            ['Correct your data', '7 days'],
            ['Delete your account', '30 days after the grace period'],
            ['Withdraw permission', 'Immediately on the website, 24 hours everywhere else'],
            ['Any complaint, absolute maximum', '90 days']
          ]},
          after: ['If we do not resolve your complaint, you can approach the Data Protection Board of India.']
        }
      ]
    },

    /* ───────── NOTIFICATIONS / WHATSAPP ───────── */
    whatsapp: {
      id: 'whatsapp', title: 'Notifications and WhatsApp policy', href: '/policies/notifications', updated: '21 Aug 2026',
      icon: 'fa-bell',
      blurb: 'Every message we send in Phase 1 — what triggers it, where it lands, how often, and how to stop it.',
      intro: 'We would rather send you three useful messages than thirty forgettable ones. This page lists every notification PickMeRight sends today, exactly as it is built. Nothing here is aspirational — if it is not on this page, we do not send it.',
      body: [
        { h: '1. Where messages reach you today',
          p: ['PickMeRight is a website, so messages land in one of three places. Browser push notifications are planned but not switched on yet; when they arrive we will update this page before we send the first one.'],
          table: { head: ['Channel', 'What it carries today', 'What it needs from you'], rows: [
            ['Notification Centre', 'Every notification below, always', 'Nothing — it is part of your account'],
            ['WhatsApp', 'Sign-in codes, your welcome message, leaderboard updates, and for vendors flagged-review reminders', 'Your number, plus a tick for anything that is not a sign-in code'],
            ['SMS and email', 'Sign-in codes only', 'Your number and email at sign-up']
          ]},
          callout: { t: 'Not yet in use', b: [
            'Browser push notifications — planned for a later release.',
            'Combined summary messages that roll several updates into one — also later. For now every event arrives on its own.'
          ]}
        },
        { h: '2. What we send you — customers',
          p: ['This is the complete list. Every one of these also appears in your Notification Centre, whether or not it reached you anywhere else.'],
          table: { head: ['Notification', 'What triggers it', 'Also on WhatsApp?', 'How often', 'When'], rows: [
            ['Sign-in code', 'You ask to sign in', 'Yes — or SMS or email', 'Every time you ask', 'Any time, day or night'],
            ['Welcome message', 'Your account is created', 'Yes', 'Once', 'Straight away'],
            ['Time to review', 'A purchase is verified and can be reviewed', 'No', 'Once, then one reminder if you have not reviewed', '7:00–10:00 PM'],
            ['A vendor replied', 'A vendor answers your review', 'No', 'Once, then one reminder', '6:00–10:00 PM'],
            ['Leaderboard movement', 'Your ranking moves meaningfully', 'Yes', 'Weekly at most', 'Weekends, 10:00 AM–1:00 PM or 7:00–9:00 PM'],
            ['A new offer for you', 'An offer matches your profile', 'No', 'Twice a week at most', '12:00–2:00 PM or 6:00–9:00 PM']
          ]},
          after: ['Offers do not go to WhatsApp. In this release they appear in your Notification Centre only.']
        },
        { h: '3. What we send you — vendors',
          table: { head: ['Notification', 'What triggers it', 'Also on WhatsApp?', 'How often', 'When'], rows: [
            ['Sign-in code', 'You ask to sign in', 'Yes — or SMS or email', 'Every time you ask', 'Any time, day or night'],
            ['Welcome message', 'Your business account is created', 'Yes', 'Once', 'Straight away'],
            ['Flagged review waiting', 'A flagged review is still unanswered', 'Yes', 'One reminder per cycle until it is resolved', '9:00–11:00 AM or 3:00–5:00 PM'],
            ['New customer request', 'A customer sends you a request', 'No', 'Twice a week at most', '9:00–11:00 AM or 3:00–6:00 PM']
          ]}
        },
        { h: '4. When we will not message you',
          p: ['Every notification above has a window, and we do not send outside it. You will not hear from us late at night or early in the morning.'],
          list: [
            'Sign-in codes are the only exception. You asked for the code, so it comes immediately, whatever the hour.',
            'Reminders sit inside the same window as the original notification. If the window has passed, the reminder waits for the next one.',
            'Weekly notifications go out at weekends, in the late morning or the early evening.'
          ]
        },
        { h: '5. How often, at most', table: { head: ['Type', 'Ceiling'], rows: [
          ['Sign-in codes', 'As many as you request'],
          ['Offers', '2 a week'],
          ['Leaderboard updates', '1 a week'],
          ['Customer requests to a vendor', '2 a week'],
          ['Review prompts and vendor replies', 'Once, plus one reminder'],
          ['Flagged-review reminders to a vendor', 'One per reminder cycle, until it is resolved']
        ]}, after: ['Caps are counted per person, not per feature. Two offers a week means two, whether they come from one vendor or six.'] },
        { h: '6. What you agreed to',
          p: ['Sign-in codes aside, every message on this page rests on something you ticked. We ask once, at sign-up, and the box is never ticked for you.'],
          consents: [
            { t: 'Stay Updated with Us', d: 'I’d like to receive helpful updates from PickMeRight through WhatsApp and / or email, including rewards, benefits, offers and other customer updates.' }
          ],
          consentNote: 'You can change your preferences at any time.',
          after: [
            'That single tick covers your welcome message, leaderboard updates, offers and — for vendors — flagged-review reminders. Leave it unticked and none of them are sent. It is one of the two permissions we ask for; the other governs personalisation and reviews, and has no effect on messaging.',
            'Two things sit outside it. Sign-in codes are sent because you asked to sign in, so there is nothing to agree to. And your Notification Centre always shows everything, because that is part of your account rather than a message pushed at you.',
            'We are building separate controls for each message type. Until they are live, the tick is all-or-nothing, and this page will change before that does.'
          ]
        },
        { h: '7. Stopping WhatsApp messages',
          p: ['In this release, WhatsApp is switched off by unsubscribing, not by a settings toggle. We are building per-message-type controls; until they are live, unsubscribing stops everything except your sign-in codes.'],
          list: [
            'Reply STOP or UNSUBSCRIBE to any PickMeRight WhatsApp message.',
            'Or write to technoreachlabs2026@gmail.com and ask us to remove your number.',
            'Either way it takes effect immediately, and within 24 hours across everything else we hold.'
          ],
          callout: { t: 'What unsubscribing does and does not do', b: [
            'It stops your welcome, leaderboard and reminder messages on WhatsApp.',
            'It does not stop sign-in codes. Those are how you get into your account, and they arrive on WhatsApp, SMS or email.',
            'It does not touch your Notification Centre. Everything still appears there, because that is part of your account rather than a message we push at you.'
          ]}
        },
        { h: '8. Your number and WhatsApp itself', p: [
          'WhatsApp is operated by Meta, not by us. When we message you there, Meta processes your phone number and the message content in order to deliver it, under its own terms.',
          'We pass your number to our messaging provider so the message can be delivered, and nothing beyond what the message itself contains. Your purchases, your scores, your reviews and your credit balance are never shared with Meta.'
        ]},
        { h: '9. What we will never do', list: [
          'Buy, rent or scrape a list of phone numbers.',
          'Message you because a vendor gave us your number.',
          'Send an offer disguised as a sign-in code or a service update.',
          'Keep messaging you after you have unsubscribed.',
          'Send outside the windows published above.'
        ]},
        { h: '10. If we get it wrong', p: ['If you are getting messages you did not agree to, or an unsubscribe has not worked, tell us at technoreachlabs2026@gmail.com or call +91 80720 46800. We treat it as a complaint rather than a support ticket, and answer within 15 days.'] },
        { h: '11. When this changes', p: ['Browser push notifications and combined summary messages are both planned. Neither is switched on. We will update this page and tell you before either starts, not afterwards.'] }
      ]
    }
  };

  constructor(private route: ActivatedRoute, private router: Router,private location: Location) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id && this.docs[id]) {
        this.activeId = id;
      } else {
        this.activeId = 'privacy';
      }
      // scroll the document panel into view on navigation, not the whole window
      queueMicrotask(() => {
        const el = document.getElementById('pmrDocPanel');
        if (el) el.scrollTop = 0;
      });
    });
  }

  get activeDoc(): PmrDoc {
    return this.docs[this.activeId];
  }

  get orderedDocs(): PmrDoc[] {
    return this.order.map(id => this.docs[id]);
  }

  selectDoc(id: string): void {
    if (id === this.activeId) { this.mobileNavOpen = false; return; }
    this.mobileNavOpen = false;
    this.router.navigate(['/policies', id], { replaceUrl: true });
  }

  onPickerChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectDoc(value);
  }

  toggleMobileNav(): void {
    this.mobileNavOpen = !this.mobileNavOpen;
  }

  goBack(): void {
    this.location.back();
  }

  trackByDocId(_: number, doc: PmrDoc): string {
    return doc.id;
  }

  isEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  getEmailLink(email: string): string {
    return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`;
  }
}